/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useMemo, useCallback } from "react";

// antd:
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Form } from "antd"; // Tag

// import type { FormProps } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getTheme, getElementState, getUser, getLng, getDict, changeAppState } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

// type RequiredMark = boolean | "optional" | "customize";

// Auxilary function:
const processChild = (child) => {
  if (!React.isValidElement(child)) { return "" }
  // Фильтруем пропсы, удаляя числовые атрибуты
  // @ts-ignore
  const filteredProps = Object.entries(child.props).reduce((acc, [key1, value1]) => {
    // Преобразуем числовые name и key1 в строки
    if ((key1 === "name" || key1 === "key") && typeof value1 === "number") {
      acc[key1] = String(value1);
    }
    // Удаляем чисто числовые атрибуты (кроме специальных разрешённых)
    else if (!/^[0-9]+$/.test(key1) || ["data-", "aria-"].some((prefix) => (key1.startsWith(prefix)))) {
      acc[key1] = value1;
    }
    return acc;
  }, {});

  // Обрабатываем вложенные children рекурсивно
  // @ts-ignore
  let newChildren = child?.props?.children; // @ts-ignore
  if (child?.props?.children) {
    // @ts-ignore
    newChildren = React.Children.map(child.props.children, (nestedChild) => (processChild(nestedChild)));
  }

  return React.cloneElement(child, { ...filteredProps, ...(newChildren ? { children: newChildren } : {}) });
}

const RZForm:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZForm.displayName = "Form";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);

  const [form] = Form.useForm();
  // const values = Form.useWatch([], form);
  // const [requiredMark, setRequiredMarkType] = useState<RequiredMark>("optional");

  const {rootAttributes, key, fullPath, layout, labelWidth, fieldsWidth} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const elState = useAppSelector(getElementState(rootAttributes.key, fullPath)); // value, visible, disabled, loading, userStyle, userClass
  const {color, backgroundColor, borders} = elState.attributes;

  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!rz.arrContains(userClss, "rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    if (elState?.loading) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      if (!~userClss.indexOf("uk-flex-middle")) { userClss.push("uk-flex-middle") }
      if (!~userClss.indexOf("uk-flex-center")) { userClss.push("uk-flex-center") }
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, elState?.loading, borders, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // Исправление: раньше onValuesChange только логировал изменения, а вызов пользовательского
  // attributes.onChange был закомментирован. Из-за этого onChange у <form> в XML фактически
  // не работал. Теперь обработчик вызывает функцию из namespace приложения и применяет
  // возвращенный Redux state так же, как это делают другие UI-компоненты.
  const onValuesChange = useCallback((changedValues, allValues) => {
    if (fnc.config?.isDev) console.log({action: "<Form key='" + key + "'> -> onValuesChange()", changedValues, allValues});
    const eventName = attributes.onChange || attributes.onchange;
    if (rootNamespace && eventName && rootNamespace[eventName]) {
      const clientFNC = rootNamespace[eventName];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, changedValues, allValues}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, changedValues, allValues});
        if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user]);

  // Global Form-API to use it inside the clients-script:
  const changeState = (params) => {
    if (params && params.fullPath && params.newState) {
      dispatch(changeAppState({appkey: params.appkey || rootAttributes.key, fullPath: params.fullPath, newState: params.newState}));
    }
  };
  const validateFormFields = (fieldsList) => new Promise((resolve) => {
    const fieldsValues = form.getFieldsValue(true);
    console.log(fieldsValues);
    form.validateFields(fieldsList).then((fields) => { resolve({result: true, fields}) }).catch((fields) => { resolve({result: false, fields}) });
  });

  // ++++++++++++++++++++++++++++++++
  useEffect(() => {
    // @ts-ignore
    if (!window.changeState) { window.changeState = changeState }
    // @ts-ignore
    if (!window.validateFormFields) { window.validateFormFields = {} }
    // @ts-ignore
    if (!window.validateFormFields[fullPath]) { window.validateFormFields[fullPath] = validateFormFields }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootAttributes.key, fullPath]);
  // ++++++++++++++++++++++++++++++++

  // @ts-ignore
  // const safeChildren = useMemo(() => {
  //   const childrenArray = React.Children.toArray(children);
  //   return childrenArray.map(processChild);
  // }, [children]);

  const cProps = useMemo(() => {
    const prps:{[key:string]:any} = { style: styl, className: clsu.join(" ") };

    if (layout) { prps.layout = layout }
    if (labelWidth && layout !== "vertical") { prps.labelCol = {span: labelWidth} }
    if (fieldsWidth && layout !== "vertical") { prps.wrapperCol = {span: fieldsWidth} }
    if (elState?.disabled || permissions.disabled === true) { prps.disabled = true }
    if (elState?.visible === false || permissions.visible === false) { prps.hidden = true }
    // Исправление: раньше onValuesChange был объявлен, но не передавался в Ant Design Form.
    // Поэтому даже реализованный обработчик не получал события изменения полей формы.
    if (attributes.onChange || attributes.onchange) { prps.onValuesChange = onValuesChange }

    return prps;
  }, [attributes.onChange, attributes.onchange, clsu, elState?.disabled, elState?.visible, fieldsWidth, labelWidth, layout, onValuesChange, permissions.disabled, permissions.visible, styl]);

  // Debug
  // useEffect(() => { console.log({attributes, elState, children, safeChildren}) }, [elState, attributes, children, safeChildren]);

  // If Form loading
  if (elState?.loading) {
    return (<div key={key + "-form-loading"} className={clsu.join(" ")} style={styl}><div className="rz-width-expand"><Spin indicator={spinIcon} /></div></div>);
  }

  return (
    <Form {...cProps} key={key + "-form"} id={key + "-form"} form={form} >
      {children}
    </Form>
  );
}

export default RZForm;
/* eslint-enable @typescript-eslint/no-unused-vars */
